/** One Stores issue, SLOC Updation or Cycle Inventory, opened on the screen its kind uses. */
import {
  isStoresCorrectionKind,
  STORES_OUTBOUND_KINDS,
  type StoresOutboundKind,
} from "@nexseed/shared";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { FormPageSkeleton } from "@/components/app/skeletons";
import { Callout } from "@/components/ui/callout";
import { useAuth } from "@/features/auth/store";
import { CorrectionPage } from "@/features/stores/components/correction-page";
import { CountPage } from "@/features/stores/components/count-page";
import { OutboundPage } from "@/features/stores/components/outbound-page";
import { SlocPage } from "@/features/stores/components/sloc-page";
import { errMsg, useStoresDoc } from "@/features/stores/hooks";

export const Route = createFileRoute("/_app/stores/docs/$id")({
  beforeLoad: () => {
    if (!useAuth.getState().can("stores.view")) throw redirect({ to: "/" });
  },
  component: Page,
});

function Page() {
  const { id } = Route.useParams();
  const q = useStoresDoc(Number(id));
  if (q.isPending) return <FormPageSkeleton />;
  if (q.isError)
    return (
      <Callout tone="danger" title="Could not open this document">
        {errMsg(q.error)}
      </Callout>
    );
  const d = q.data;
  const key = `${d.id}-${d.status}`;
  if (d.kind === "sloc")
    return (
      <SlocPage
        key={key}
        doc={d}
        facilityId={d.facilityId}
        plantName={d.facility?.name ?? null}
      />
    );
  if (d.kind === "cycle_count") return <CountPage key={key} doc={d} />;
  if (isStoresCorrectionKind(d.kind))
    return (
      <CorrectionPage
        key={key}
        kind={d.kind}
        doc={d}
        facilityId={d.facilityId}
        plantName={d.facility?.name ?? null}
      />
    );
  if (d.kind === "opening")
    return (
      <Callout tone="info" title={`Opening Stock ${d.docNo ?? ""}`}>
        Loaded from a sheet: see Stores ▸ Opening Stock for its rows, print and
        cancel.
      </Callout>
    );
  if ((STORES_OUTBOUND_KINDS as readonly string[]).includes(d.kind))
    return (
      <OutboundPage
        key={key}
        doc={d}
        kind={d.kind as StoresOutboundKind}
        facilityId={d.facilityId}
        plantName={d.facility?.name ?? null}
      />
    );
  return (
    <Callout tone="warning" title="Not shown here">
      This is a Stores arrival; open it from Stores ▸ Arrival.
    </Callout>
  );
}
